import React, { useEffect, useState, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";
import { parseISO, format, isValid } from "date-fns";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CContainer,
  CRow,
  CSpinner,
  CAlert,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CBadge,
} from "@coreui/react";
import settingsStore from "../../store/SettingsStore";
import dangerousEventsStore from "../../store/DangerousEventsStore";
import cashShiftsStore from "../../store/CashShiftsStore";
import PeriodFilters from "../UI/PeriodFilters/PeriodFilters";
import ServerSelect from "../UI/PeriodFilters/ServerSelect";
import { formatDangerousEventType } from "../../utils/dangerousEventTypes";
import { fmtInt } from "../../utils/chartHelpers";
import { formatYmd } from "../../utils/dateRange";
import { logReportView } from "../../api/reportLogApi";
import InsightsPanel from "../Insights/InsightsPanel";
import { buildOpsInsights } from "../../utils/insightsEngine";

function dayStartIso(dateStr) {
  return `${dateStr}T00:00:00.000`;
}

function dayEndExclusiveIso(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}T00:00:00.000`;
}

function formatEventDate(dateStr) {
  if (!dateStr) return "—";
  let d = parseISO(String(dateStr).trim());
  if (!isValid(d)) d = parseISO(String(dateStr).replace(" ", "T"));
  return isValid(d) ? format(d, "dd.MM.yyyy HH:mm") : String(dateStr);
}

const OpsSummaryPage = observer(() => {
  const [selectedServer, setSelectedServer] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [quickRange, setQuickRange] = useState("week");

  const load = useCallback(
    async (from, to) => {
      if (!selectedServer || !from || !to) return;
      await Promise.all([
        dangerousEventsStore.fetchEvents(
          selectedServer,
          dayStartIso(from),
          dayEndExclusiveIso(to),
          { employeesIncludeDeleted: true },
        ),
        cashShiftsStore.fetchShifts(selectedServer, from, to, {
          status: "ANY",
          employeesIncludeDeleted: true,
        }),
      ]);
    },
    [selectedServer],
  );

  useEffect(() => {
    const init = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) await settingsStore.getServers(userId);
      const today = new Date();
      const past = new Date(today);
      past.setDate(today.getDate() - 7);
      setFromDate(formatYmd(past));
      setToDate(formatYmd(today));
      logReportView({
        event: "report_view",
        reportKey: "ops-summary",
        reportTitle: "Операционный контроль",
        userId,
        path: "/ops-summary",
      });
    };
    init();
  }, []);

  useEffect(() => {
    if (!settingsStore.servers?.length) return;
    if (!selectedServer) setSelectedServer(String(settingsStore.servers[0].id));
  }, [settingsStore.servers, selectedServer]);

  useEffect(() => {
    if (!selectedServer || !fromDate || !toDate) return;
    load(fromDate, toDate);
  }, [selectedServer, fromDate, toDate, load]);

  const loading = dangerousEventsStore.isLoading || cashShiftsStore.isLoading;
  const evSummary = dangerousEventsStore.summary;
  const cashSummary = cashShiftsStore.summary;

  const problemRows = useMemo(() => {
    const rows = [];
    for (const ev of dangerousEventsStore.events.slice(0, 15)) {
      let sum = "—";
      for (const a of ev.attributes || []) {
        if (a.name === "sum") sum = a.value;
      }
      rows.push({
        key: `ev-${ev.id}`,
        date: formatEventDate(ev.date),
        type: formatDangerousEventType(ev),
        sum,
        link: "/dangerous-ops",
        badge: "warning",
      });
    }
    for (const d of cashShiftsStore.analytics.diffs.slice(0, 10)) {
      rows.push({
        key: `cs-${d.shift.id}`,
        date: formatEventDate(d.shift.openDate),
        type: `Расхождение кассы №${d.shift.sessionNumber}`,
        sum: `${fmtInt(d.cashDiff)} ₽`,
        link: "/cash-shifts",
        badge: "danger",
      });
    }
    return rows;
  }, [dangerousEventsStore.events, cashShiftsStore.analytics.diffs]);

  const opsInsights = useMemo(() => {
    if (loading) return [];
    return buildOpsInsights({
      evSummary,
      cashSummary,
      analytics: cashShiftsStore.analytics,
      events: dangerousEventsStore.events,
    });
  }, [
    loading,
    evSummary.count,
    evSummary.totalSum,
    cashSummary.diffCount,
    cashSummary.warningCount,
    cashShiftsStore.analytics,
    dangerousEventsStore.events,
  ]);

  return (
    <CContainer style={{ flex: "1", marginTop: "120px" }}>
      <h4 className="mb-1">Операционный контроль</h4>
      <p className="text-body-secondary mb-3">Сводка рисков и кассовых расхождений</p>
      <ServerSelect
        servers={settingsStore.servers}
        value={selectedServer}
        onChange={(e) => setSelectedServer(e.target.value)}
      />
      <PeriodFilters
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
        onRefresh={load}
        maxDays={90}
        quickRange={quickRange}
        onQuickRangeChange={setQuickRange}
        disabled={loading}
      />
      {(dangerousEventsStore.errorMessage || cashShiftsStore.errorMessage) && (
        <CAlert color="danger" className="mt-3">
          {dangerousEventsStore.errorMessage || cashShiftsStore.errorMessage}
        </CAlert>
      )}
      {loading && (
        <div className="text-center py-4">
          <CSpinner />
        </div>
      )}
      {!loading && (
        <>
          <InsightsPanel
            insights={opsInsights}
            title="Рекомендации по контролю"
            className="insights-panel--ops-summary"
          />
          <CRow className="g-3 mt-2 mb-4">
            <CCol md={6}>
              <CCard className="h-100">
                <CCardHeader className="d-flex justify-content-between">
                  <span>Опасные операции</span>
                  <Link to="/dangerous-ops" className="btn btn-sm btn-outline-warning">
                    Подробнее
                  </Link>
                </CCardHeader>
                <CCardBody>
                  <div className="fs-3 fw-semibold">{evSummary.count}</div>
                  <div className="text-body-secondary">событий за период</div>
                  <div className="mt-2">Сумма: {fmtInt(evSummary.totalSum)} ₽</div>
                </CCardBody>
              </CCard>
            </CCol>
            <CCol md={6}>
              <CCard className="h-100">
                <CCardHeader className="d-flex justify-content-between">
                  <span>Кассовые смены</span>
                  <Link to="/cash-analytics" className="btn btn-sm btn-outline-primary">
                    Аналитика
                  </Link>
                </CCardHeader>
                <CCardBody>
                  <div className="fs-3 fw-semibold">{cashSummary.diffCount}</div>
                  <div className="text-body-secondary">смен с расхождением</div>
                  <div className="mt-2">
                    Подозрительных: {cashSummary.warningCount} · Сумма расхождений:{" "}
                    {fmtInt(cashSummary.totalCashDiff)} ₽
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
          <CCard>
            <CCardHeader>Последние проблемы</CCardHeader>
            <CCardBody>
              <CTable small hover responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Дата</CTableHeaderCell>
                    <CTableHeaderCell>Тип</CTableHeaderCell>
                    <CTableHeaderCell>Сумма</CTableHeaderCell>
                    <CTableHeaderCell />
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {problemRows.length === 0 && (
                    <CTableRow>
                      <CTableDataCell colSpan={4} className="text-center text-body-secondary">
                        Проблем не обнаружено
                      </CTableDataCell>
                    </CTableRow>
                  )}
                  {problemRows.map((r) => (
                    <CTableRow key={r.key}>
                      <CTableDataCell>{r.date}</CTableDataCell>
                      <CTableDataCell>
                        <CBadge color={r.badge} className="me-2">
                          {r.badge === "warning" ? "Событие" : "Касса"}
                        </CBadge>
                        {r.type}
                      </CTableDataCell>
                      <CTableDataCell>{r.sum}</CTableDataCell>
                      <CTableDataCell>
                        <Link to={r.link} className="btn btn-sm btn-link">
                          Открыть
                        </Link>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        </>
      )}
    </CContainer>
  );
});

export default OpsSummaryPage;
